import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MailWarning, Loader2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { normalizeCheckoutError } from "@/lib/checkoutError";

const VerificationGateContext = createContext(null);

const COOLDOWN_SECONDS = 60;
const VALID_ACTIONS = new Set(["claim", "download", "purchase"]);

/**
 * Global "Email verification required" modal used by every checkout entry
 * point. Any component can call `openVerificationGate({ email?, action? })`
 * to surface the same persistent, mobile-safe UI — no more one-line
 * toasts that scroll off screen the moment the customer taps the button.
 *
 * `action` selects the localised subtitle:
 *   - "claim"    → free-track claim messaging
 *   - "download" → download-guarded messaging
 *   - "purchase" → paid checkout messaging (default)
 *
 * The Resend button reuses the existing
 * `POST /api/auth/resend-verification` flow (which queues via the Email
 * Center outbox), inherits the server-side rate limit (3 / 15 min per
 * ip+email), and adds a 60-second client-side cooldown so an impatient
 * user cannot spam the button before the mail arrives.
 */
export function VerificationGateProvider({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState(null);
  const [action, setAction] = useState("purchase");
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [error, setError] = useState(null);
  const cooldownTimerRef = useRef(null);
  const { t } = useTranslation();

  const openVerificationGate = useCallback((opts = {}) => {
    setOverrideEmail(opts.email || null);
    setAction(VALID_ACTIONS.has(opts.action) ? opts.action : "purchase");
    setError(null);
    setOpen(true);
  }, []);

  const closeVerificationGate = useCallback(() => setOpen(false), []);

  const email = overrideEmail || user?.email || "";

  // Body-scroll lock while modal is visible.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeVerificationGate(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeVerificationGate]);

  // Cooldown countdown.
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      return;
    }
    if (!cooldownTimerRef.current) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldown((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }
    return () => {
      if (cooldownTimerRef.current && cooldown === 1) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, [cooldown]);

  const onResend = useCallback(async () => {
    if (!email) {
      setError(t("verifyEmail.noEmail"));
      return;
    }
    if (cooldown > 0 || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post("/auth/resend-verification", { email });
      setLastSentAt(new Date());
      setCooldown(COOLDOWN_SECONDS);
      toast.success(t("verifyEmail.resendSuccessToast"));
    } catch (err) {
      const { message } = normalizeCheckoutError(err, t("verifyEmail.defaultError"));
      setError(message);
    } finally {
      setSending(false);
    }
  }, [email, cooldown, sending, t]);

  const value = useMemo(() => ({
    openVerificationGate,
    closeVerificationGate,
    isOpen: open,
  }), [openVerificationGate, closeVerificationGate, open]);

  return (
    <VerificationGateContext.Provider value={value}>
      {children}
      {open && typeof document !== "undefined" && createPortal(
        <VerificationModal
          email={email}
          action={action}
          sending={sending}
          cooldown={cooldown}
          lastSentAt={lastSentAt}
          error={error}
          onResend={onResend}
          onClose={closeVerificationGate}
        />,
        document.body,
      )}
    </VerificationGateContext.Provider>
  );
}

export function useVerificationGate() {
  const ctx = useContext(VerificationGateContext);
  if (!ctx) {
    // Defensive: return a noop so unwrapped tests don't crash. In prod
    // the provider is mounted at App root so this branch is unreachable.
    return { openVerificationGate: () => {}, closeVerificationGate: () => {}, isOpen: false };
  }
  return ctx;
}

function VerificationModal({ email, action, sending, cooldown, lastSentAt, error, onResend, onClose }) {
  const { t } = useTranslation();
  const disabled = sending || cooldown > 0;
  const btnLabel = sending
    ? t("verifyEmail.resendSending")
    : cooldown > 0
      ? t("verifyEmail.resendIn", { s: cooldown })
      : t("verifyEmail.resendLabel");

  const subtitleKey =
    action === "claim"
      ? "verifyEmail.gateSubtitleClaim"
      : action === "download"
        ? "verifyEmail.gateSubtitleDownload"
        : "verifyEmail.gateSubtitlePurchase";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-gate-title"
      data-testid="verify-gate-modal"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-6"
    >
      <div
        data-testid="verify-gate-panel"
        className="w-full sm:max-w-md bg-[#0a0a0a] border border-red-brand/50 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-5 sm:p-6 border-b border-white/10 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-brand/15 flex items-center justify-center shrink-0">
            <MailWarning size={20} className="text-red-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="verify-gate-title"
              data-testid="verify-gate-title"
              className="font-anton text-2xl leading-tight text-white"
            >
              {t("verifyEmail.gateTitle")}
            </h2>
            <p className="mt-1 text-sm text-white/70" data-testid="verify-gate-subtitle">
              {t(subtitleKey)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="verify-gate-close"
            aria-label={t("verifyEmail.closeAria")}
            className="text-white/60 hover:text-white p-2 -mr-2 -mt-2 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div className="hairline rounded-xl p-3 bg-white/[0.03]">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              {t("verifyEmail.emailLabel")}
            </div>
            <div
              data-testid="verify-gate-email"
              className="mt-1 text-white font-semibold break-all"
              dir="ltr"
            >
              {email || "—"}
            </div>
          </div>

          <ul className="text-sm text-white/75 space-y-1.5 ps-1">
            <li>{t("verifyEmail.inboxTip1")}</li>
            <li>{t("verifyEmail.inboxTip2")}</li>
            <li>{t("verifyEmail.inboxTip3")}</li>
          </ul>

          {lastSentAt && !error && (
            <div className="hairline rounded-xl p-3 bg-emerald-500/10 border-emerald-500/40 text-emerald-200 text-sm flex items-start gap-2" data-testid="verify-gate-sent">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              <span>
                {t("verifyEmail.sentBanner", { when: formatWhen(lastSentAt, t) })}
              </span>
            </div>
          )}

          {error && (
            <div
              data-testid="verify-gate-error"
              className="hairline rounded-xl p-3 bg-red-brand/10 border-red-brand/50 text-red-brand text-sm"
            >
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={onResend}
            disabled={disabled}
            data-testid="verify-gate-resend"
            className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            {btnLabel}
          </button>

          <p className="text-[11px] font-mono text-white/40 text-center tracking-wide leading-relaxed">
            {t("verifyEmail.gateFooter")}
          </p>
        </div>
      </div>
    </div>
  );
}

function formatWhen(d, t) {
  try {
    const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (diff < 5) return t("verifyEmail.sentJustNow");
    if (diff < 60) return t("verifyEmail.sentSecAgo", { n: diff });
    if (diff < 3600) return t("verifyEmail.sentMinAgo", { n: Math.floor(diff / 60) });
    return d.toLocaleTimeString();
  } catch {
    return t ? t("verifyEmail.sentJustNow") : "just now";
  }
}
